// Data for the Plan tab: tonight's routine, the sleep forecast and a running sleep timer.
// Refreshed every minute and when the app comes back to the foreground.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useOpenEntries } from '@/features/track/store';
import type { Step } from './steps';

export type Routine = {
  date: string;
  steps: Step[];
  done: { stepId: string; at: string; by: string }[];
  currentStepId: string | null;
  finished: boolean;
  bedtime: string;
  startAt: string;
  minutesLeft: number;
};

export type Forecast = {
  state: { status: 'asleep'; kind: 'nap' | 'night'; since: string; expectedWakeAt: string } | { status: 'awake'; since: string; awakeMinutes: number; atNight: boolean } | { status: 'unknown' };
  nextSleep: { kind: 'nap' | 'bedtime'; at: string; napNumber: number | null; reason: string; remindAt: string | null; expectedDurationMin?: number } | null;
  schedule: { kind: 'nap' | 'bedtime'; at: string }[];
  confidence: 'low' | 'medium' | 'high';
};

export type Entry = { id: string; type: string; startAt: string; endAt: string | null };

// The last plan this phone loaded, per baby: shown at once on the next open while the fresh one loads.
// Kept for a few hours only: the forecast and tonight's routine go out of date.
const CACHE_MAX_AGE = 3 * 60 * 60_000;
type Cached = { at: number; routine: Routine; forecast: Forecast };
const cacheKey = (babyId: string) => `bp:plan:${babyId}`;
function readCached(babyId: string | undefined): Cached | null {
  if (!babyId) return null;
  try {
    const c = JSON.parse(localStorage.getItem(cacheKey(babyId)) ?? 'null') as Cached | null;
    return c && Date.now() - c.at < CACHE_MAX_AGE ? c : null;
  } catch { return null; }
}
function writeCached(babyId: string, routine: Routine, forecast: Forecast) {
  try { localStorage.setItem(cacheKey(babyId), JSON.stringify({ at: Date.now(), routine, forecast })); } catch { /* private mode, full */ }
}

export function usePlan(babyId: string | undefined) {
  const [routine, setRoutine] = useState<Routine | null>(() => readCached(babyId)?.routine ?? null);
  const [forecast, setForecast] = useState<Forecast | null>(() => readCached(babyId)?.forecast ?? null);
  // The running sleep comes from the offline tracker store, so a timer started on Track (even offline) shows here at once.
  const open = useOpenEntries(babyId);
  const runningSleep: Entry | null = open.find((x) => x.type === 'sleep' && !x.endAt && !x.deletedAt) ?? null;
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!babyId) return;
    try {
      const [r, p] = await Promise.all([
        api<{ routine: Routine }>(`/api/babies/${babyId}/routine`),
        api<{ forecast: Forecast }>(`/api/babies/${babyId}/plan`),
      ]);
      if (!alive.current) return;
      setRoutine(r.routine);
      setForecast(p.forecast);
      setError(null);
      writeCached(babyId, r.routine, p.forecast);
    } catch (err) {
      if (alive.current) setError((err as { code?: string }).code ?? 'generic');
    }
  }, [babyId]);

  // another baby picked: show its saved plan (or the loading state), not the previous baby's
  const shownFor = useRef(babyId);
  useEffect(() => {
    if (shownFor.current === babyId) return;
    shownFor.current = babyId;
    const c = readCached(babyId);
    setRoutine(c?.routine ?? null);
    setForecast(c?.forecast ?? null);
  }, [babyId]);

  useEffect(() => {
    alive.current = true;
    void load();
    const iv = window.setInterval(() => void load(), 60_000);
    const onVis = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive.current = false; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  return { routine, setRoutine, forecast, runningSleep, error, reload: load };
}
